import { formatDate } from "./dateFormat";
import { isNotNeededInterval } from "./taskStatus";
import { period } from "./periodEnd";
import {
  getHighestAssessmentNumber,
  getHighestApplicationLetter,
} from "./util";
/**
 * Normalize various date inputs to a local midnight Date to avoid timezone/UTC off-by-one issues.
 * @param {string|number|Date} input
 * @returns {Date|null}
 */
const toNormalizedLocalDate = (input) => {
  if (!input) return null;

  // Already a valid Date
  if (input instanceof Date && !isNaN(input.getTime())) {
    return new Date(input.getFullYear(), input.getMonth(), input.getDate());
  }

  if (typeof input === "string") {
    // YYYY-MM-DD should be treated as local date (not UTC)
    const m = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const y = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10) - 1;
      const d = parseInt(m[3], 10);
      return new Date(y, mo, d);
    }
    const parsed = new Date(input);
    if (!isNaN(parsed.getTime())) {
      return new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate()
      );
    }
    return null;
  }

  if (typeof input === "number") {
    const parsed = new Date(input);
    if (!isNaN(parsed.getTime())) {
      return new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate()
      );
    }
  }
  return null;
};
/**
 * Enrich a raw diary/task entry with the derived fields the dashboard cards need:
 * dueDate (the Planned date if set, else from the Time interval), dueStart/dueEnd (the first and last day of that
 * week, month position or season; else the dueDate), a resolved task name, and the BBCH the card shows
 * (cardBBCH, see getCardBBCH).
 * @param {Object} rawTask - The raw diary entry to enrich.
 * @param {Array} diaries - The full array of diary entries for the trial (for context lookups).
 * @returns {Object} - The enriched task object.
 */
const enrichTask = (rawTask, diaries) => {
  // Agrolab's Planned value, when it gives a date (its planned_date, else the date of what was chosen, e.g.
  // "medio May 2027"), is the date the task is due by - also outside the customer's expected date, which is
  // left as it is. Planned in words without a date falls back to the Time interval. A Time interval in words
  // ("14 DAA", "BBCH 31-32", "mid Dec") gives no date: it is never counted from an application or guessed.
  const plannedDue = rawTask?.planned_date
    ? toNormalizedLocalDate(rawTask.planned_date)
    : rawTask?.planned_interval ? parseTimeInterval(rawTask.planned_interval) : null;
  let dueDate = plannedDue || parseTimeInterval(rawTask?.timeinterval);

  // Defensive normalization: ensure dueDate is local-midnight Date. If it can't be normalized
  // (e.g. an Invalid Date slipped through), resolve to null rather than keeping the stray
  // invalid value - `TaskList.jsx` relies on `dueDate === null` to always show a task
  // regardless of the selected week, and a lingering Invalid Date would silently break that.
  if (dueDate) {
    dueDate = toNormalizedLocalDate(dueDate);
  }

  // The days the due value covers (a week Monday to Sunday, medio the 11th-20th; a date itself), for the status only:
  // This week from the first, overdue only after the last, as in the Excel report (utils/periodEnd.js)
  const dueValue = plannedDue ? rawTask.planned_interval || rawTask.planned_date : rawTask?.timeinterval;
  const span = dueDate ? period(dueValue) : null;
  const dueStart = span?.start ?? dueDate;
  const dueEnd = span?.end ?? dueDate;

  // if task name is "?" assign a name based on vano
  assignTaskName(rawTask);

  return {
    ...rawTask,
    dueDate,
    dueStart,
    dueEnd,
    cardBBCH: getCardBBCH(diaries, rawTask),
  };
};

/**
 * Find the next task in the diaries.
 * @param {Array} diaries - The array of diary entries.
 * @returns {Object|null} - The next task object or null if not found.
 */
const getNextTask = (diaries) => {
  if (!diaries || diaries.length === 0) return null;

  const nextTask = getFirstTask(diaries);
  if (!nextTask) return null; // if no more tasks, return null

  return enrichTask(nextTask, diaries);
};

/** An imported 00 visit: the old system's placeholder (made outside the app: no created_by). */
const isImportedV00 = (diary) => diary?.vano === "00" && diary?.created_by == null;

/**
 * Get every open task that should get its own dashboard card for a trial. Unlike
 * `getNextTask`/`getFirstTask` (a single-slot sequencer that hides everything behind
 * whichever task comes first in `taskOrder`), this returns every Visit (00 too, also an
 * imported one: V00 is a real visit, as in TIS) and Application that's still open, plus every
 * open Activity except ones the vano-code catalog marks as not meant to be shown
 * (`visible: false`) - so a trial can show an open activity, application and visit all at
 * once, each as its own card. `isInfo` activities (Reserved Area, Plot Size) are shown too
 * (customer point 72).
 * @param {Array} diaries - The array of diary entries.
 * @param {Array} [vanoCodes] - The VA-code catalog (GET /v1/masterdata/vano-codes shape,
 *   see `utils/vanoCodes.js`), used only to filter Activities (va_id 8) by `visible`.
 *   Visits and Applications have no catalog entry and are never filtered by it. If
 *   omitted, no catalog-based filtering is applied.
 * @returns {Array} - The list of enriched task objects (possibly empty).
 */
const getDashboardTasks = (diaries, vanoCodes) => {
  if (!diaries || diaries.length === 0) return [];

  const catalogByVano = new Map((vanoCodes || []).map((c) => [c.vano, c]));

  const tasks = diaries.filter((d) => {
    if (!d || d.cancelled || d.donedate !== null) return false;
    // Visits (1), Applications (2), Activities (8) - the only types with a dashboard
    // card; matches what `getFirstTask`'s `taskOrder` could ever select.
    const vaId = Number(d.va_id);
    if (![1, 2, 8].includes(vaId)) return false;
    if (isNotNeededInterval(d)) return false;
    if (vaId === 8) {
      const entry = catalogByVano.get(d.vano);
      // No catalog match (e.g. a manually-added Activity, vano "8") -> keep it, same as
      // Visits/Applications. Only a hidden catalog entry excludes it.
      if (entry && !entry.visible) return false;
    }
    return true;
  });

  return tasks.map((task) => enrichTask(task, diaries));
};

/**
 * Whether a task is "mine" for the given user, and why - shared by the dashboard's mine/all
 * filter and its per-card badge so the two can never disagree.
 *
 * A direct assignee always wins: if the task has ANY assignees, only that assignment counts -
 * your trial role is not a fallback for a task explicitly given to someone else. Only a task
 * with NO assignee at all falls back to your role (Study Director/Field Technician/Principal
 * Investigator) on the trial.
 *
 * When currentUserId is null (id not yet resolved - see auth.js), this degrades to "mine" with
 * no badge rather than excluding everything, so the dashboard shows its normal contents instead
 * of appearing to have lost all tasks while the id is still loading.
 * @param {Object} trial - trial object (needs the raw studydirector/FT/PI id columns).
 * @param {Object} task - an enriched task/diary object (needs `assignees`, from Diary::assignees).
 * @param {number|string|null|undefined} currentUserId
 * @returns {{ isMine: boolean, badgeText: string|null }}
 */
const getMineInfo = (trial, task, currentUserId) => {
  if (currentUserId == null) return { isMine: true, badgeText: null };

  const uid = String(currentUserId);
  const assignees = Array.isArray(task?.assignees) ? task.assignees : [];

  if (assignees.length > 0) {
    const assignedToMe = assignees.some((a) => String(a?.id) === uid);
    return assignedToMe
      ? { isMine: true, badgeText: "Assigned to you" }
      : { isMine: false, badgeText: null };
  }

  if (trial?.studydirector != null && String(trial.studydirector) === uid) {
    return { isMine: true, badgeText: "SD" };
  }
  if (trial?.FT != null && String(trial.FT) === uid) {
    return { isMine: true, badgeText: "FT" };
  }
  if (trial?.PI != null && String(trial.PI) === uid) {
    return { isMine: true, badgeText: "PI" };
  }

  return { isMine: false, badgeText: null };
};

/**
 * Get the next diary/task in the order where donedate is null.
 * @param {Array} diaries
 * @returns {Object|null}
 */
const getFirstTask = (diaries) => {
  if (!diaries || diaries.length === 0) return null;

  // Define priority tasks first
  // stacked out and borderspray
  const priorityTasks = ["A11", "A13"];

  // Generate A-Z letters
  const letters = Array.from({ length: 26 }, (_, i) =>
    String.fromCharCode(65 + i)
  );

  // Generate 0-99 numbers (with leading zeros for single digits)
  const numbers = Array.from({ length: 100 }, (_, i) =>
    i.toString().padStart(2, "0")
  );

  // Define specific tasks at the end
  const specificTasks = [
    "A34",
    "A32",
    "A33",
    "A42",
    "A43",
    "A29",
    "A44",
    "A35",
  ];

  // Combine all task orders: priority → letters → numbers → specific
  const taskOrder = [
    ...priorityTasks,
    ...letters,
    ...numbers,
    ...specificTasks,
  ];

  // find redundant V00 diary, remove if found
  let skipV00 = diaries.find(
    (diary) =>
      (diary.vano === "01" && diary.donedate) ||
      (diary.vano === "A" && diary.donedate)
  );
  if (skipV00) {
    diaries = diaries.filter((diary) => !isImportedV00(diary));
  }

  let skipNotNeeded = diaries.find(
    (diary) => diary.timeinterval === "Not needed"
  );
  if (skipNotNeeded) {
    diaries = diaries.filter((diary) => diary.timeinterval !== "Not needed");
  }

  // Find the first incomplete task in order
  for (const vano of taskOrder) {
    const task = diaries.find(
      (diary) =>
        diary.vano === vano && diary.donedate === null && !diary.cancelled
    );
    // If this is an application (single letter), ensure previous application's assessments are completed
    if (task && /^[A-Z]$/.test(task.vano)) {
      const letter = task.vano;
      const prevCode = letter.charCodeAt(0) - 1;
      if (prevCode >= 65) {
        const prevLetter = String.fromCharCode(prevCode);
        // Find numeric assessment diaries that reference the previous application letter
        const prevAssessments = diaries.filter((d) => {
          if (!d || !d.timeinterval) return false;
          const appLetter = getApplicationLetter(d.timeinterval);
          const isAssessment = /^[0-9]{2}$/.test(d.vano);
          return (
            isAssessment &&
            appLetter === prevLetter &&
            d.timeinterval !== "Not needed" &&
            !d.cancelled
          );
        });
        // If any previous assessment exists and is not done, skip current application
        const missing = prevAssessments.some((d) => !d.donedate);
        if (missing) {
          continue; // skip this application until previous assessments are completed
        }
      }
    }

    // check if task doesnt contain a letter, therefore it's an assessment
    if (task && !/[A-Z]/.test(task.vano)) {
      // find associated application for assessment
      let associatedApplication = diaries.find(
        (diary) => diary.vano === getApplicationLetter(task?.timeinterval)
      );
      // check if application has been completed
      let isApplicationDone = associatedApplication?.donedate !== null;
      if (isApplicationDone) {
        // if application is done, return the assessment
        return task;
      } else {
        // else return the application
        return associatedApplication;
      }
    } else if (task) {
      return task;
    }
  }

  return null;
};

/**
 * The BBCH a task card shows, with the done date of the task it was recorded on ({ bbch, doneDate }, doneDate null
 * when that task is not done): the task's own BBCH (also before it is done), else the trial's latest - of its done,
 * not cancelled tasks with a BBCH, the one done last up to today, else the soonest after. null when there is none.
 * @param {Array} diaries - The trial's diary entries.
 * @param {Object} task - The card's task.
 */
const getCardBBCH = (diaries, task) => {
  if (task?.bbch) return { bbch: task.bbch, doneDate: task.donedate ? toNormalizedLocalDate(task.donedate) : null };

  const today = toNormalizedLocalDate(new Date());
  const candidates = (diaries || [])
    .filter((d) => d && d.donedate && d.bbch && !d.cancelled)
    .map((d) => ({ bbch: d.bbch, doneDate: toNormalizedLocalDate(d.donedate) }))
    .filter((c) => c.doneDate);
  const past = candidates.filter((c) => c.doneDate <= today).sort((a, b) => b.doneDate - a.doneDate);
  if (past.length > 0) return past[0];
  return candidates.sort((a, b) => a.doneDate - b.doneDate)[0] ?? null;
};

/**
 * Get the application letter from a time interval string.
 * @param {string} timeinterval - The time interval string (e.g., "7 DAA", "2 WAB").
 * @returns {string|null} - The application letter or null if not found.
 */
const getApplicationLetter = (timeinterval) => {
  if (!timeinterval) return null;
  // take input like "7 DAA", "2 WAB", "10 DA-A" and return the last letter. Only treat the
  // second token as a code when the first is numeric - otherwise arbitrary descriptive text
  // (e.g. "mid NOV", "at flowering") would spuriously yield a fake "application letter" from
  // its last character (e.g. "mid NOV" -> "V").
  const parts = String(timeinterval).trim().split(/\s+/);
  if (parts.length < 2 || !/^\d+$/.test(parts[0])) return null;
  return parts[1].slice(-1);
};

// Capitalized for display/canonical-string construction (e.g. "primo November 2026") - matching
// is always done case-insensitively (see `parseApproximateComponents`), so this casing is purely
// cosmetic but should stay consistent everywhere the string is shown or re-emitted.
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * The Monday of a given ISO week number/year - used to resolve "week 45 2026"-style
 * approximate timing to an actual Date.
 * @param {number} week
 * @param {number} year
 * @returns {Date}
 */
const getDateOfISOWeek = (week, year) => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dayOfWeek = simple.getDay();
  const isoWeekStart = new Date(simple);
  if (dayOfWeek <= 4) {
    isoWeekStart.setDate(simple.getDate() - dayOfWeek + 1);
  } else {
    isoWeekStart.setDate(simple.getDate() + 8 - dayOfWeek);
  }
  return isoWeekStart;
};

/**
 * Parse a canonical approximate-timing string into its raw component parts. This is the single
 * source of truth for the shape produced by the Approximate mode in DiaryEntryModal.jsx /
 * TimeIntervalDialog.jsx: "primo|medio|ultimo <Month> <Year>" (Danish agronomy convention -
 * start/middle/end of month) or "week <N> <Year>". Both `parseApproximateTimeInterval` (below,
 * for due-date computation) and `ApproximateTimingSelect.jsx` (for populating its dropdowns when
 * editing an existing entry) build on this, rather than duplicating the regexes/month list.
 * @param {string} timeinterval
 * @returns {{mode: 'month', position: string, monthIndex: number, year: string}
 *   | {mode: 'week', week: string, year: string} | null} - null if it doesn't match either shape.
 */
const parseApproximateComponents = (timeinterval) => {
  const s = String(timeinterval || "").trim();

  const monthMatch = s.match(/^(primo|medio|ultimo)\s+([A-Za-z]+)\s+(\d{4})$/i);
  if (monthMatch) {
    const monthIndex = MONTH_NAMES.findIndex(
      (m) => m.toLowerCase() === monthMatch[2].toLowerCase()
    );
    if (monthIndex !== -1) {
      return {
        mode: "month",
        position: monthMatch[1].toLowerCase(),
        monthIndex,
        year: monthMatch[3],
      };
    }
  }

  const weekMatch = s.match(/^week\s+(\d{1,2})\s+(\d{4})$/i);
  if (weekMatch) {
    return { mode: "week", week: weekMatch[1], year: weekMatch[2] };
  }

  return null;
};

/**
 * Parse the structured "approximate timing" formats (see `parseApproximateComponents`) into an
 * approximate Date.
 * @param {string} timeinterval
 * @returns {Date|null} - null if `timeinterval` doesn't match either shape.
 */
const parseApproximateTimeInterval = (timeinterval) => {
  const parsed = parseApproximateComponents(timeinterval);
  if (!parsed) return null;

  if (parsed.mode === "month") {
    const year = parseInt(parsed.year, 10);
    if (parsed.position === "primo") return new Date(year, parsed.monthIndex, 1);
    if (parsed.position === "medio") return new Date(year, parsed.monthIndex, 15);
    if (parsed.position === "ultimo") return new Date(year, parsed.monthIndex + 1, 0); // last day
  }

  if (parsed.mode === "week") {
    const weekNumber = parseInt(parsed.week, 10);
    const year = parseInt(parsed.year, 10);
    if (weekNumber >= 1 && weekNumber <= 53) return getDateOfISOWeek(weekNumber, year);
  }

  return null;
};

/**
 * Compact display label for the structured "approximate timing" formats (see
 * `parseApproximateComponents`), e.g. "Medio Nov '26", "Week 45 '26" - used by the dashboard
 * card badge in place of a precise due-date countdown, since these are intentionally fuzzy.
 * @param {string} timeinterval
 * @returns {string|null} - null when `timeinterval` isn't one of those shapes.
 */
const getApproximateLabel = (timeinterval) => {
  const parsed = parseApproximateComponents(timeinterval);
  if (!parsed) return null;

  if (parsed.mode === "month") {
    const positionLabel = parsed.position[0].toUpperCase() + parsed.position.slice(1);
    return `${positionLabel} ${MONTH_ABBR[parsed.monthIndex]} '${parsed.year.slice(-2)}`;
  }

  return `Week ${parseInt(parsed.week, 10)} '${parsed.year.slice(-2)}`;
};

/**
 * Whether a timeinterval string holds a BBCH growth-stage value (e.g. "BBCH 89",
 * "BBCH 10-18", "bbch 30") rather than a date. BBCH is a crop growth stage, not a calendar
 * date - and unlike other unparseable free text, the native `Date` parser (and chrono) can
 * misread the digits inside one as a real date instead of failing (e.g. "BBCH 89" -> 1 Jan
 * 1989, "BBCH 10-18" -> 18 Oct 2001), so every date-parsing attempt below must check this
 * first rather than let it happen. A BBCH interval legitimately has no due date until
 * someone assigns one - same as "at infestation" or an empty interval.
 * @param {string} timeinterval
 * @returns {boolean}
 */
const isBbchInterval = (timeinterval) => /bbch/i.test(String(timeinterval || ""));

/**
 * The date a Time interval or Planned value gives: a full date (any way formatDate reads one), a month
 * position or a week - the same values the Excel report reads as dates. Anything else is words and gives
 * no date ("7 DAA", "BBCH 89", "mid Dec", "after harvest"); words are never guessed as a date.
 * @param {string} timeinterval - e.g. "2027-04-15", "15th April 2027", "medio May 2027", "week 23 2027".
 * @returns {Date|null}
 */
const parseTimeInterval = (timeinterval) => {
  if (!timeinterval) return null;

  // BBCH values are not dates - checked before any parsing attempt below (see isBbchInterval).
  if (isBbchInterval(timeinterval)) return null;

  // Structured "approximate timing" (primo/medio/ultimo + month, or week number) - checked
  // first since these are unambiguous canonical formats produced by the Approximate mode.
  const approxDate = parseApproximateTimeInterval(timeinterval);
  if (approxDate) return toNormalizedLocalDate(approxDate);

  // DD-MM-YYYY (the convention used throughout this app's UI, e.g. "25/07") is ambiguous to
  // the native Date parser - `new Date("05-08-2026")` is read as May 8 (US month-day-year),
  // not the intended Aug 5. Handle it explicitly, unambiguously, before falling through.
  const ddmmyyyy = String(timeinterval)
    .trim()
    .match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (ddmmyyyy) {
    const day = parseInt(ddmmyyyy[1], 10);
    const monthRaw = parseInt(ddmmyyyy[2], 10);
    const year = parseInt(ddmmyyyy[3], 10);
    // Validate month (1-12) and day (1-31) before building the Date - previously any 2-digit
    // "month" was accepted, so e.g. "07/25/2024" (day 07, "month" 25) silently overflowed to
    // Jan 07 2026 instead of failing safely.
    if (monthRaw >= 1 && monthRaw <= 12 && day >= 1 && day <= 31) {
      const d = new Date(year, monthRaw - 1, day);
      // Also guard against day-of-month rollover (e.g. day 31 in a 30-day month).
      if (!isNaN(d.getTime()) && d.getMonth() === monthRaw - 1) {
        return toNormalizedLocalDate(d);
      }
    }
    // else: not a date - fall through to the full dates formatDate reads
  }

  // A full date written out ("2027-04-15", "15th April 2027", "April 15, 2027", "15.04.2027"), as formatDate
  // shows it (DD-MM-YYYY); anything else is words
  const written = formatDate(timeinterval).match(/^(\d{2})-(\d{2})-(\d{4})$/);
  return written ? new Date(Number(written[3]), Number(written[2]) - 1, Number(written[1])) : null;
};

/**
 * Assign a name to the task based on its properties.
 * @param {Object} task - The task object.
 * @returns {Object|null} - The updated task object or null if not found.
 */
const assignTaskName = (task) => {
  if (!task) return null;

  let taskName = task?.task;
  if (taskName === "?") {
    const vano = task?.vano;
    // regex to check if vano is a two-digit number eg. an assessment
    if (/^\d{2}$/.test(vano)) {
      taskName = `Assessment ${parseInt(vano, 10)}`;
      // regex to check if vano is a letter eg. an application
    } else if (/^[A-Z]$/.test(vano)) {
      taskName = `Application ${vano}`;
    } else {
      taskName = "Unknown task";
    }
    task.task = taskName;
  }
};

/**
 * Check if the completed task is the last task for the current stage.
 * @param {Object} trial - The trial object containing trial details.
 * @param {Object} completedTask - The task that was just completed.
 * @param {Array} diaries - The array of all diary entries for the trial.
 * @param {string} currentStage - The current stage of the trial.
 * @returns {boolean} - True if this is the last task for the current stage.
 */
const isLastTaskForStage = (trial, completedTask, diaries, currentStage) => {
  if (!completedTask || !diaries || !currentStage) return false;

  const stage = currentStage.toLowerCase();

  // For Setup stage
  if (stage === "setup") {
    // Check if this is task "13" (borderspray) and it's now completed
    if (completedTask.vano === "A13") {
      return true; // Borderspray completion means setup is done
    } else if (
      completedTask.vano === "A11" &&
      trial.Borders_Sprayed === false
    ) {
      return true;
    }
    // check if completed task is "A11" (stacked out), and borderspray has timeinterval "Not needed", or borderspray doesnt exist
    // } else if (completedTask.vano === "A11") {
    //   const bordersprayDiary = diaries.find((diary) => diary.vano === "A13");
    //   if (bordersprayDiary && (bordersprayDiary.timeinterval === "Not needed" || trial.Borders_Sprayed === false)) {
    //     return true;
    //   } else if (!bordersprayDiary) {
    //     return true;
    //   }
    // }

    return false;
  }

  // For Application/Assessment stage
  if (stage === "application/assessment") {
    const letterIndex = getHighestApplicationLetter(diaries).index;
    const letters = Array.from({ length: letterIndex }, (_, i) =>
      String.fromCharCode(65 + i)
    );
    const numberOfAssessments = getHighestAssessmentNumber(diaries);
    const numbers = Array.from({ length: numberOfAssessments }, (_, i) =>
      i.toString().padStart(2, "0")
    );

    // Combine all task orders: priority → letters → numbers → specific
    const taskOrder = [...letters, ...numbers];

    const completedTaskIndex = taskOrder.indexOf(completedTask.vano);
    if (completedTaskIndex === -1) return false;

    // Check if there are any incomplete application (A-Z) or assessment (0-99) tasks
    // that come after the completed task in the task order
    const remainingTasksAfterCompleted = taskOrder
      .slice(completedTaskIndex + 1)
      .filter((vano) => {
        // Only consider application (A-Z) or assessment (0-99) tasks
        const isApplicationOrAssessment =
          /^[A-Z]$/.test(vano) || /^\d{2}$/.test(vano);

        if (!isApplicationOrAssessment) return false;

        const diary = diaries.find((d) => d.vano === vano);
        return (
          diary &&
          diary.donedate === null &&
          diary.timeinterval !== "Not needed"
        );
      });

    return remainingTasksAfterCompleted.length === 0;
  }

  // For Harvest stage
  if (stage === "harvest") {
    // Check completion status of each harvest task
    const pegsUpTask = diaries.find((diary) => diary.vano === "A34");
    const harvestTask = diaries.find((diary) => diary.vano === "A32");
    const destructionTask = diaries.find((diary) => diary.vano === "A33");

    const pegsUpDone = pegsUpTask?.donedate !== null;
    const harvestDone = harvestTask?.donedate !== null;
    const destructionDone = destructionTask?.donedate !== null;

    // Determine which tasks are required based on trial settings
    const harvestRequired = trial.Harvest !== false;
    const destructionRequired = trial.Destruction !== false;

    // Check if all required harvest tasks are complete
    const requiredTasksComplete =
      pegsUpDone &&
      (!harvestRequired || harvestDone) &&
      (!destructionRequired || destructionDone);

    return requiredTasksComplete;
  }

  // Report is the last stage: a trial is never closed by completing a task, only by a user (utils/trialClose.js;
  // Final Report and Trial Done ask whether to close it)
  if (stage === "report") {
    return false;
  }
};

/**
 * The date a task is ordered by: its done date once done, otherwise its due date (as for the
 * dashboard: the Planned date, else the Time interval's date). null when it has neither.
 */
const taskDate = (task, diaries) =>
  task?.donedate
    ? toNormalizedLocalDate(task.donedate)
    : enrichTask({ ...task }, diaries).dueDate;

// Same-date (or dateless) tasks: by number (visits 00, 01 ... / applications A, B ...), then by
// creation, so a newly added one comes last
const compareVano = (a, b) => {
  const va = String(a?.vano ?? "");
  const vb = String(b?.vano ?? "");
  if (/^\d+$/.test(va) && /^\d+$/.test(vb)) return Number(va) - Number(vb);
  return va.localeCompare(vb);
};
/** Order of tasks with the same (or no) date: by number, then by creation (id). */
const compareSameDate = (a, b) => compareVano(a, b) || (Number(a?.id) || 0) - (Number(b?.id) || 0);

/**
 * Tasks in chronological order: by date (done date, else due date), tasks without a date last;
 * ties by number, then by creation (id) - a new task goes to the bottom of its group.
 * @param {Array} tasks - the tasks to sort (e.g. one group: the visits).
 * @param {Array} [diaries] - all the trial's diary entries, for due dates relative to others.
 * @returns {Array} - a new, sorted array.
 */
const sortTasks = (tasks, diaries = tasks) =>
  (tasks || [])
    .map((task) => ({ task, date: taskDate(task, diaries) }))
    .sort((a, b) => {
      if (a.date && b.date && a.date.getTime() !== b.date.getTime()) return a.date - b.date;
      if (a.date && !b.date) return -1;
      if (!a.date && b.date) return 1;
      return compareSameDate(a.task, b.task);
    })
    .map(({ task }) => task);
// Natural order of task numbers: visits 00, 01 ... 10 (numbers), applications A ... Z, AA ...
// (letters: shorter first), anything else after them, by text
const vanoRank = (vano) => {
  const v = String(vano ?? "").trim();
  if (/^\d+$/.test(v)) return [0, Number(v), ""];
  if (/^[A-Z]+$/i.test(v)) return [1, v.length, v.toUpperCase()];
  return [2, 0, v];
};
const compareByNumber = (a, b) => {
  const [ga, na, ta] = vanoRank(a?.vano);
  const [gb, nb, tb] = vanoRank(b?.vano);
  return ga - gb || na - nb || ta.localeCompare(tb) || (Number(a?.id) || 0) - (Number(b?.id) || 0);
};

/**
 * Tasks by their number - visits 00, 01 ... 10, applications A ... Z, AA ... - for the diary list
 * (the date only decides the status). A new entry gets the next number, so it comes last.
 */
const sortByNumber = (tasks) => [...(tasks || [])].sort(compareByNumber);

/**
 * Activities in the VA-code catalog's own order (the old system's order within each category);
 * entries the catalog does not know come after, by number.
 */
const sortByCatalog = (tasks, vanoCodes) => {
  const position = new Map((vanoCodes || []).map((code, index) => [code.vano, index]));
  const rank = (task) => position.get(task?.vano) ?? Number.MAX_SAFE_INTEGER;
  return [...(tasks || [])].sort((a, b) => rank(a) - rank(b) || compareByNumber(a, b));
};
export default getNextTask;
export {
  isLastTaskForStage,
  enrichTask,
  getApplicationLetter,
  getDashboardTasks,
  getMineInfo,
  sortTasks,
  sortByNumber,
  sortByCatalog,
  compareSameDate,
  getApproximateLabel,
  parseApproximateComponents,
  parseTimeInterval,
  isBbchInterval,
  MONTH_NAMES,
};
